//src/pages/InsuranceRequestDetail.jsx (collectionneur)
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useTranslation } from "react-i18next";

function fmtDate(d, locale) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(d);
  }
}

function fmtDateOnly(d, locale) {
  if (!d) return "";
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString(locale, { dateStyle: "medium" });
  } catch {
    return String(d);
  }
}

function badgeClass(status) {
  const s = (status || "").toLowerCase();
  if (s === "pending") return "bg-amber-100 text-amber-800 border-amber-200";
  if (s === "seen") return "bg-blue-100 text-blue-800 border-blue-200";
  if (s === "answered" || s === "quoted") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (s === "accepted") return "bg-green-100 text-green-800 border-green-200";
  if (s === "rejected") return "bg-red-100 text-red-800 border-red-200";
  if (s === "done") return "bg-gray-100 text-gray-700 border-gray-200";
  return "bg-gray-100 text-gray-700 border-gray-200";
}

function statusKey(status) {
  const value = (status || "").toLowerCase();
  return ["pending", "seen", "answered", "quoted", "accepted", "rejected", "done"].includes(value)
    ? value
    : "unknown";
}


export default function InsuranceRequestDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "de" ? "de-DE" : i18n.resolvedLanguage === "en" ? "en-GB" : "fr-FR";

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [row, setRow] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setErr("");

      const { data, error } = await supabase
        .from("insurance_requests")
        .select(
          "id,created_at,updated_at,status,request_type,insured_value,currency,notes,payload,partner_message,quote_amount,quote_currency,coverage_context,policy_kind,event_mode,event_start_date,event_end_date,transport_needed,storage_location"
        )
        .eq("id", id)
        .single();

      if (error) throw error;
      setRow(data);
    } catch (e) {
      console.error(e);
      setErr(e?.message || t("insuranceRequestDetail.errors.load"));
      setRow(null);
    } finally {
      setLoading(false);
    }
  };

  const setDecision = async (decision) => {
    try {
      setErr("");
      const nextStatus = decision === "accept" ? "accepted" : "rejected";
      const { error } = await supabase
        .from("insurance_requests")
        .update({ status: nextStatus })
        .eq("id", id);
      if (error) throw error;
      await load();
    } catch (e) {
      console.error(e);
      setErr(e?.message || t("insuranceRequestDetail.errors.updateStatus"));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-sm text-gray-500">{t("insuranceRequestDetail.loading")}</div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="p-8 space-y-4 max-w-3xl mx-auto">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50"
        >
          {t("insuranceRequestDetail.actions.back")}
        </button>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <div className="text-gray-500">{t("insuranceRequestDetail.notFound")}</div>
      </div>
    );
  }

  const p = row.payload || {};
  const a = p.artwork || {};
  const title = a.title || p.artwork_title || t("insuranceRequestDetail.artworkFallback");
  const artist = a.artist || p.artwork_artist || "";

  const coverageLabel = row.coverage_context
    ? t(`insuranceRequestDetail.coverage.${row.coverage_context}`, { defaultValue: "—" })
    : "—";

  const policyLabel = row.policy_kind
    ? t(`insuranceRequestDetail.policy.${row.policy_kind}`, { defaultValue: row.policy_kind })
    : null;

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6">
      {/* Header avec statut et explication */}
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-2xl font-serif font-semibold text-gray-900">{t("insuranceRequestDetail.header.title")}</div>
            <div className="text-gray-600 mt-1">{t("insuranceRequestDetail.header.subtitle")}</div>
          </div>
          <div className="text-3xl">🛡️</div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className={`text-xs px-3 py-1.5 rounded-lg border font-medium ${badgeClass(row.status)}`}>
            {t(`insuranceRequestDetail.status.${statusKey(row.status)}`, { defaultValue: row.status })}
          </span>
          <span className="text-xs text-gray-500">{t("insuranceRequestDetail.header.createdAt", { date: fmtDate(row.created_at, locale) })}</span>
        </div>

        {/* Message d'explication du statut */}
        <div className="mt-4 text-sm text-gray-700 bg-white/60 rounded-lg p-3 border border-gray-200">
          💬 {t(`insuranceRequestDetail.statusExplanation.${statusKey(row.status)}`)}
        </div>
      </div>

      {/* Bloc pédagogique : Ce que le courtier analyse */}
      <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-5">
        <div className="flex items-start gap-3">
          <div className="text-2xl">🔍</div>
          <div className="flex-1 text-sm">
            <div className="font-semibold text-gray-900 mb-2">
              {t("insuranceRequestDetail.analysis.title")}
            </div>
            <div className="text-gray-700 space-y-1.5">
              <p>{t("insuranceRequestDetail.analysis.body")}</p>
              <p className="text-xs text-gray-600 italic">{t("insuranceRequestDetail.analysis.note")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Détails de l'œuvre et du contexte */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xl font-medium text-gray-900">
              {title} {artist ? <span className="text-gray-500">• {artist}</span> : null}
            </div>
            <div className="text-sm text-gray-500 mt-1">{t("insuranceRequestDetail.details.requestType")}: {t(`insuranceRequestDetail.requestType.${row.request_type}`, { defaultValue: row.request_type })}</div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="text-gray-700">
            <span className="text-gray-500">{t("insuranceRequestDetail.details.insuredValue")}:</span>{" "}
            {row.insured_value !== null && row.insured_value !== undefined
              ? `${Number(row.insured_value).toLocaleString(locale)} ${row.currency || "EUR"}`
              : "—"}
          </div>
          <div className="text-gray-700">
            <span className="text-gray-500">{t("insuranceRequestDetail.details.context")}:</span> {coverageLabel}
          </div>
        </div>

        {/* Contexte assurance (métier) */}
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 space-y-2">
          <div className="font-medium text-gray-900 mb-2">{t("insuranceRequestDetail.coverageDetails.title")}</div>
          {policyLabel && (
            <div>
              <span className="text-gray-500">{t("insuranceRequestDetail.coverageDetails.policyType")}:</span>{" "}
              <span className="font-medium">{policyLabel}</span>
            </div>
          )}
          {row.storage_location && (
            <div>
              <span className="text-gray-500">{t("insuranceRequestDetail.coverageDetails.storageLocation")}:</span> {row.storage_location}
            </div>
          )}
          {row.transport_needed && (
            <div className="flex items-center gap-2">
              <span>🚚</span>
              <span>{t("insuranceRequestDetail.coverageDetails.transportRequired")}</span>
            </div>
          )}
          {(row.event_start_date || row.event_end_date) && (
            <div>
              <span className="text-gray-500">{t("insuranceRequestDetail.coverageDetails.eventPeriod")}:</span>{" "}
              {row.event_start_date ? fmtDateOnly(row.event_start_date, locale) : "—"} → {row.event_end_date ? fmtDateOnly(row.event_end_date, locale) : "—"}
            </div>
          )}
        </div>

        {/* Informations partagées avec le courtier */}
        {(p.context || row.notes) && (
          <div className="text-sm text-gray-700">
            <div className="text-gray-900 font-medium mb-2">{t("insuranceRequestDetail.sharedInfo.title")}</div>
            <div className="text-xs text-gray-600 mb-2 italic">
              {t("insuranceRequestDetail.sharedInfo.description")}
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 whitespace-pre-wrap">
              {p.context || row.notes}
            </div>
          </div>
        )}
      </div>

      {/* Réponse du partenaire */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 space-y-5">
        <div className="text-xl font-medium text-gray-900 flex items-center gap-2">
          <span>📬</span>
          <span>{t("insuranceRequestDetail.partner.title")}</span>
        </div>

        {row.partner_message || row.quote_amount ? (
          <>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-gray-700">
              <div className="flex items-start gap-2">
                <span className="text-lg">💡</span>
                <div>
                  <p className="font-medium text-gray-900 mb-1">{t("insuranceRequestDetail.partner.noticeTitle")}</p>
                  <p>{t("insuranceRequestDetail.partner.noticeBody")}</p>
                </div>
              </div>
            </div>

            {row.partner_message && (
              <div>
                <div className="text-sm font-medium text-gray-900 mb-2">{t("insuranceRequestDetail.partner.message")}</div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 whitespace-pre-wrap">
                  {row.partner_message}
                </div>
              </div>
            )}

            {row.quote_amount !== null && row.quote_amount !== undefined && (
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="text-sm text-gray-500 mb-1">{t("insuranceRequestDetail.partner.quoteAmount")}</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {Number(row.quote_amount).toLocaleString(locale)} {row.quote_currency || "EUR"}
                </div>
                <div className="text-xs text-gray-600 mt-2">
                  💬 {t("insuranceRequestDetail.partner.percentage", {
                    percentage: row.insured_value
                      ? ((row.quote_amount / row.insured_value) * 100).toLocaleString(locale, { maximumFractionDigits: 2 })
                      : "—",
                  })}
                </div>
              </div>
            )}

            {(row.status === "quoted" || row.status === "answered") && (
              <div className="space-y-3 pt-2">
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setDecision("accept")}
                    className="px-5 py-2.5 rounded-xl bg-black text-white font-medium hover:bg-gray-800 transition-all"
                  >
                    {t("insuranceRequestDetail.actions.accept")}
                  </button>
                  <button
                    onClick={() => setDecision("reject")}
                    className="px-5 py-2.5 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 transition-all"
                  >
                    {t("insuranceRequestDetail.actions.reject")}
                  </button>
                </div>
                <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 border border-gray-200">
                  ℹ️ <strong>{t("insuranceRequestDetail.partner.importantTitle")}</strong> {t("insuranceRequestDetail.partner.importantBody")}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-5 text-sm text-gray-700">
            <div className="flex items-start gap-3">
              <div className="text-2xl">⏳</div>
              <div>
                <p className="font-medium text-gray-900 mb-1">{t("insuranceRequestDetail.pending.title")}</p>
                <p>{t("insuranceRequestDetail.pending.body")}</p>
                <p className="text-xs text-gray-600 mt-2 italic">{t("insuranceRequestDetail.pending.note")}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions bas de page */}
      <div className="flex flex-wrap gap-3 pt-2">
        <button
          onClick={() => navigate("/insurance/requests")}
          className="px-5 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-all"
        >
          {t("insuranceRequestDetail.actions.backToRequests")}
        </button>
        <button
          onClick={load}
          className="px-5 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-all"
        >
          {t("insuranceRequestDetail.actions.refresh")}
        </button>
      </div>

      {/* Footer pédagogique : Relativiser le prix de la prime */}
      <div className="rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 to-white p-5">
        <div className="flex items-start gap-3">
          <div className="text-2xl">💬</div>
          <div className="text-sm text-gray-700 space-y-1.5">
            <p className="font-medium text-gray-900">{t("insuranceRequestDetail.footer.title")}</p>
            <p>{t("insuranceRequestDetail.footer.body")}</p>
            <p className="text-xs text-gray-600 italic">{t("insuranceRequestDetail.footer.note")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}